<script setup>
const props = defineProps({
  course: { 
    type: Object,
    required: true
  },
  selection: {
    type: String,
    default: 'All'
  },
  openInNewTab: {
    type: Boolean,
    default: true
  },
  locationIds: {
    type: String,
    default: []
  }
})

const locations = ref(props.locationIds)
const validateLocations = () => {
  return locations.value.length > 0 && locations.value.some((location) => props.course.pulseCourse.locations.includes(location) ) && props.selection === 'All';
}
const format = ref(Array.isArray(locations.value) && !validateLocations() && props.selection !== 'On-Campus' ? 'Online' : props.selection);

const changeFormat = (value, locationIds) => {
  format.value = value;   
  if (locationIds && locationIds.length > 0 && !locationIds.some((location) => props.course.pulseCourse.locations.includes(location) ) && value === 'All') {
    format.value = 'Online'; 
  }
}

defineExpose({
  changeFormat    
})

const { genres, ages, skillLevels, duration } = useCourseData();
const newBadgeText = 'NEW'
const newBadgeColor = 'bg-new-badge'
// Initialize badge properties to empty values
let badgeColor, badgeText = '';
const showBadge = (course) => {
  // Define a mapping of course types to badge information
  const badgeMapping = [
    {
      type: 'isVTC',
      badgeColor: 'bg-virtual-camp-badge',
      badgeText: 'Virtual Camp',
    },
    {
      type: 'isOnlinePrivateLesson',
      badgeColor: 'bg-private-lessons-badge',
      badgeText: 'Private Lessons',
    },
    {
      type: 'isOTA',
      badgeColor: 'bg-teen-academy-badge',
      badgeText: 'Teen Academy',
    },
    {
      type: 'isTechAcademies',
      badgeColor: 'bg-teen-academy-badge',
      badgeText: 'Teen Academy',
    },
    {
      type: 'isTechCamps',
      badgeColor: 'bg-tech-camp-badge',
      badgeText: 'Tech Camp',
    },
  ];

  // Find the matching badge information based on the course type
  for (const mapping of badgeMapping) {
    if (course[mapping.type]) {
      badgeColor = mapping.badgeColor;
      badgeText = mapping.badgeText;
      break; // Exit the loop once a match is found
    }
  }

  return { badgeColor, badgeText };
};

const showNewBadge = (course) => {
  if(course.pulseCourse.isNew) {
    return true;
  }
  return false;
}

const durationBasedOnDivision = (course) => {
  if (course.pulseCourse.division.id === 25) {
    // Display text before the comma
    return duration(course.pulseCourse).split(',')[0]; 
  } else if (course.pulseCourse.division.id === 41) {
    let variantProducts = course.variants
      .filter(product => product !== null && product.visibilityTypeId !== 330003 && product.visibilityTypeId !== 330004)
      .map(variantProduct => variantProduct.quantity);

    if (variantProducts.length) {
      variantProducts.sort((a, b) => a - b);
      variantProducts = variantProducts.join(', ');
    } else {
      variantProducts = course.pulseCourse.quantity;
    }
    return variantProducts + "-pack available" + (duration(course.pulseCourse).split('lessons,'))
  } else {
    return duration(course.pulseCourse);
  }
};

const onCampusBtn = ref(props.course.isIPC)
const onlineBtn = ref(props.course.hasOnlineCourse || props.course.isVirtual)

const setTargetSelf = () => {
  document.querySelectorAll('.course-link').forEach((el) => {
    el.target = '_self';
  })
}

const setTargetBlank = () => {
  document.querySelectorAll('.course-link').forEach((el) => {
    el.target = '_blank';
  })
}


const changeTargetOnResize = () => {
  if (window.innerWidth <= 768) {
    setTargetSelf()
  }
  else if (props.openInNewTab) {
    setTargetBlank();
  }
}

const forceOnlineFormat = () => {  
  
  if (props.forceOnline) {
    format.value = 'Online'
  }
}

onMounted(() => {
  if (format.value === '') {
    format.value = 'All'
  }
  onCampusBtn.value = props.course.isIPC
  onlineBtn.value = (props.course.hasOnlineCourse && props.course.relatedCourse) || props.course.isVirtual

  window.addEventListener("resize", (event) => {
    changeTargetOnResize()
  });

  changeTargetOnResize()
})

const onCampusBtnClass = computed(() => {
  return {
    'btn-primary': onCampusBtn.value && (format.value === 'All' || format.value === 'On-Campus'),
    'btn-secondary': onCampusBtn.value && format.value === 'Online'
  }
})

const onlineBtnClass = computed(() => {
  return {
    'btn-primary': (onlineBtn.value && ((format.value === 'All' && !onCampusBtn.value) || format.value === 'Online')) || (onlineBtn.value && !onCampusBtn.value)  ,
    'btn-secondary': onlineBtn.value && onCampusBtn.value && (format.value === 'On-Campus' || format.value === 'All')
  }
})

const titleLink = computed(() => {
  return onCampusBtn.value && format.value === 'Online' && props.course.relatedCourse ? `/courses/${props.course.relatedCourse.slug}` : `/courses/${props.course.slug}`;
})
const onlineLink = props.course.hasOnlineCourse && props.course.relatedCourse ? `/courses/${props.course.relatedCourse.slug}` : `/courses/${props.course.slug}`

</script>

<template>
  <div class="card" >
    <NuxtImg :src="course.image !== null ? course.image.url : 'https://via.placeholder.com/640x360'" class="card-image w-100" />
    
    <div class="card-body p-0">
      <div class="badges my-2">
        <span :class="`badge ${badgeColor} me-2`" v-if="showBadge(course)" v-html="badgeText"></span>
        <span :class="`badge badge ${newBadgeColor} me-2`" v-if="showNewBadge(course)" v-html="newBadgeText"></span>
        <!-- <span class="badge bg-light text-dark">Popular</span> -->
      </div>
      <h6 class="card-title"><NuxtLink :href="titleLink" class="">{{ course.title }}</NuxtLink></h6>
      <p class="card-text mb-0">{{ ages(course.pulseCourse) }}</p>
      <p class="card-text mb-0">{{ skillLevels(course.pulseCourse, true) }}</p>
    </div>
    <div class="card-footer p-0 mt-2 d-flex justify-content-start justify-content-xxl-start justify-content-lg-between">
      <NuxtLink :to="`/courses/${course.slug}`" class="btn me-1 course-link" :class="onCampusBtnClass" v-if="onCampusBtn">On-Campus
      </NuxtLink>
      <NuxtLink :to="onlineLink" class="btn course-link" :class="onlineBtnClass"  v-if="onlineBtn">Virtual</NuxtLink>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card {
  border-radius: 24px;
  padding: 10px;
  max-width: 310px;
  height: 100%;

  .card-footer {
    border: none;
    background-color: $color-secondary
  }

  @include media-breakpoint-up(xl) {
    width: 100%;
    max-width: 330px;
  }

  @include media-breakpoint-between(lg, xl) {
    width: 100%;
    max-width: 272px;
    margin: 0 auto;
  }

  @include media-breakpoint-down(md) {
    max-width: 360px;
    margin: 0 auto;
  }

  @include media-breakpoint-down(sm) {
    max-width: 366px;
    margin: 0 auto;
    padding: 14px;
  }
  

  .card-image {    
    border-radius: 12px;
    height: 146px;
    object-fit: cover;

    @include media-breakpoint-up(xxl) {
      height: 169px;
    }

    @include media-breakpoint-between(xl, xxl) {
      height: 143px;
    }

    @include media-breakpoint-between(lg, xl) {
      height: 146px;
    }

    @include media-breakpoint-between(md, lg) {
      height: 165px;
    }

    @include media-breakpoint-down(md) {
      height: 133px;
    }
    
    @include media-breakpoint-down(sm) {
      height: 197px;
    }
  }

  h6 {
    line-height: 1.56;
    a {
      text-decoration: none;
      color: $color-primary;
      font-size: 1rem;
      font-weight: 600;
    }
  }

  .badges .badge {
    font-weight: 600;
    font-size: 12px;
  }

  .btn-primary {
    color: $color-secondary;
    padding: 8px 20px;
    @include media-breakpoint-between(sm, md) {
      font-size: 14px;
    }
  }

  .btn-secondary {
    padding: 8px 20px;
    @include media-breakpoint-between(sm, md) {
      font-size: 14px;
    }
  }

  .card-text {
    color: $color-subtext;
  }
}
</style>